import './policy-manager-container.scss';
import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { PolicySummary } from '../../policy-summary/policy-summary';
import CustomIcon, { IconType } from 'src/components/shared/custom-icon/custom-icon';
import { PolicyManagerActions } from './policy-manager-actions';
import { useStores } from '../../../store';
import { ServiceDto } from '../../../../dtos/customer.dto';
import { FwVersionInfo } from '../../../../dtos/version.dto';
import { SelectConnection } from './select-connection';
import isEqual from 'lodash/isEqual';
import { LevelName, VersionStatus } from '../../../../utils/enums';

interface PolicyManagerContainerProps {
    visible: boolean;
    close: () => void;
}

export const PolicyManagerContainer: React.FC<PolicyManagerContainerProps> = observer(({ visible, close }) => {
    const { fwVersionStore, customerStore } = useStores();
    const [selectedConnection, setSelectedConnection] = useState<ServiceDto | undefined>();
    const [selectedVersion, setSelectedVersion] = useState<FwVersionInfo | undefined>(undefined);
    const [versionsList, setVersionsList] = useState<FwVersionInfo[] | undefined>(undefined);
    const panelRef = useRef<HTMLDivElement | null>(null);

    const handleClickOutside = (event: any) => {
        const antPortalElements = document.querySelectorAll('.ant-dropdown, .ant-modal, .ant-select-dropdown, .ant-popover, .ant-modal-wrap');
        const isClickInsidePortal = Array.from(antPortalElements).some(element => element.contains(event.target));

        if (panelRef.current && !panelRef.current.contains(event.target) && !isClickInsidePortal) {
            close();
        }
    };

    useEffect(() => {
        if (visible) {
            setSelectedConnection(customerStore.selectedLocation);
            document.addEventListener('mousedown', handleClickOutside);
        }
        else {
            setSelectedConnection(undefined);
            setSelectedVersion(undefined);
            setVersionsList(undefined);
            document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [visible]);

    useEffect(() => {
        if (!selectedConnection) return;
        const isDiffFromCurrentConnection = selectedConnection.serviceId !== customerStore.serviceId;
        const connectionVersionsList = fwVersionStore.allFwVersions[selectedConnection.serviceId];
        const selectedConnectionVersionsList = connectionVersionsList?.filter(_ =>
            _.serviceLevel !== LevelName.BRONZE.toLowerCase() && !(isDiffFromCurrentConnection && _.versionStatus === VersionStatus.PUSH_ERROR));
        if (!isEqual(selectedConnectionVersionsList, versionsList)) setVersionsList(selectedConnectionVersionsList);
    }, [selectedConnection, fwVersionStore.allFwVersions]);

    useEffect(() => {
        if (!versionsList) {
            setSelectedVersion(undefined);
            return;
        }
        const currentVersionId = fwVersionStore.selectedFwVersion?.externalId;
        setSelectedVersion(versionsList.find(_ => _.externalId === (selectedVersion?.externalId || currentVersionId)) || versionsList[0]);
    }, [versionsList]);

    return <div className={`policy-manager-container ${visible ? 'visible' : ''}`} ref={panelRef}>
        <div className="header">
            <div className="title">Policy Manager</div>
            <CustomIcon type={IconType.X} onClick={close} size={10} />
        </div>
        <div className="content">
            <SelectConnection selectedConnection={selectedConnection}
                              setConnection={setSelectedConnection}
                              versionsList={versionsList}
                              selectedVersion={selectedVersion}
                              setVersion={setSelectedVersion}/>
            {selectedVersion &&
              <PolicySummary serviceLevel={customerStore.selectedLocation?.serviceLevel}
                             externalId={selectedVersion.externalId}
                             comments={selectedVersion.comments}
                             connectionId={selectedVersion.sid}
              >
                <div className="policy-summary-header">
                  <div className="title">Policy Summary</div>
                  <PolicyManagerActions selectedConnection={selectedConnection}
                                        selectedVersion={selectedVersion}
                                        closePanel={close}/>
                </div>
              </PolicySummary>}
        </div>
    </div>;
});
